/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import HCaptcha from "@hcaptcha/react-hcaptcha";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslations } from "@i18n/react";
import { useConsent } from "@packages/consent";
import type { PasswordAuthFlowForm } from "@packages/sdk";
import {
  passwordAuthFlowSchema,
  preexistingPasswordAuthFlowSchema,
  useRequestAuth,
} from "@packages/sdk";
import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";

import { spacing } from "../../../../global/emotion";
import { useIsDesktopViewport } from "../../../hooks/useIsDesktopViewport";
import { useValidator } from "../../../hooks/useValidator.emotion";
import { cssMediaQueryMinWidthTablet } from "../../../lib/constants/emotionMediaQueries.constants";
import { Button } from "../../_base/_core/Button/Button.emotion";
import { IconButton } from "../../_base/_core/IconButton/IconButton.emotion";
import { Text } from "../../_base/_core/Text/Text.emotion";
import { EyeIcon } from "../../_base/icons/Icon/GeneralIcons";
import { useAlert } from "../../_base/modals/Dialog/Dialog.emotion";
import { FormField } from "../forms/wrapper/FormField.emotion";
import { AuthError } from "./AuthError.emotion";
import { EmailHeader } from "./AuthFlowHeader.emotion";
import { AuthForm } from "./AuthForm.emotion";
import { AuthSubmitButton } from "./AuthSubmitButton.emotion";
import { AuthTextInput } from "./AuthTextInput.emotion";
import type { AuthFlowProps } from "./AuthTypes.emotion";

const buttonsStyle: Interpolation = {
  alignItems: "center",
  display: "flex",
  flexDirection: "column-reverse",
  [cssMediaQueryMinWidthTablet]: {
    flexDirection: "row",
    justifyContent: "space-between",
  },
  gap: spacing.ms,
  justifyContent: "flex-end",
};
const justOneStyle: Interpolation = {
  justifyContent: "flex-end",
};
const inputFormFieldStyle: Interpolation = {
  width: spacing.full,
};

export const PasswordVisibilityButton = ({
  onClick,
  visible,
}: {
  onClick: () => void;
  visible: boolean;
}) => {
  return (
    <IconButton
      icon={visible ? <EyeIcon.Off /> : <EyeIcon.On />}
      onClick={onClick}
      type="button"
      size="m"
      variant="transparentPrimary"
    />
  );
};

export const PasswordAuthFlowWithSignup = ({
  onAuth,
  setFlow,
  state,
  utm,
  onFormSubmit,
  onCaptchaVerify: reportVerification,
  onError,
  formDisabled = false,
  hideHeader,
  className = "",
}: AuthFlowProps) => {
  const t = useTranslations();
  const { voluntaryConsent } = useConsent();
  const alert = useAlert();
  const { mutationRegister, mutationLogin, mutationForgotPassword } =
    useRequestAuth();
  const [visible, setVisible] = useState(false);
  const isDesktop = useIsDesktopViewport();
  const captchaRef = useRef(null);
  const { email, exists } = state;
  const { resolver, errors, errorsCSS, fieldsCSS, inputProps } =
    useValidator<PasswordAuthFlowForm>(
      zodResolver(
        exists ? preexistingPasswordAuthFlowSchema : passwordAuthFlowSchema,
      ),
    );
  const resolveRegister = useRef<(() => void) | null>(null);

  useEffect(() => {
    if (!email) setFlow("email");
  }, [email]);

  const form = useForm<PasswordAuthFlowForm>({ resolver });

  const onSubmit = async ({ password }: PasswordAuthFlowForm) => {
    onFormSubmit?.({ email, ...utm });
    try {
      // If email registration, check captcha value
      if (!exists) {
        captchaRef.current.execute();
        return new Promise<void>(
          (resolve) => (resolveRegister.current = resolve),
        );
      } else {
        const data = await mutationLogin.mutateAsync({
          email,
          password,
          consent: voluntaryConsent ?? undefined,
        });
        await onAuth?.({ data });
      }
    } catch (error) {
      await alert.show({
        title: t("general_word_error"),
        description: error.message,
      });
      onError?.(error);
    }
  };

  const onCaptchaVerify = async (captcha: string) => {
    const { password } = form.getValues();

    reportVerification?.({ email, ...utm });

    try {
      const data = await mutationRegister.mutateAsync({
        email,
        password,
        captcha,
        ...utm,
        consent: voluntaryConsent ?? undefined,
      });
      await onAuth?.({ data });
      resolveRegister.current?.();
    } catch (error) {
      await alert.show({
        title: t("general_word_error"),
        description: error.message,
      });
      onError?.(error);
      resolveRegister.current?.();
    }
  };

  const onCaptchaError = async () => {
    await alert.show({
      title: t("general_word_error"),
      description: t("error_registration_failure"),
    });
    onError?.(null);
  };

  const handlePasswordReset = async () => {
    try {
      await mutationForgotPassword.mutateAsync({ email });

      await alert.show({
        title: t("general_word_success_exclamation"),
        description: t("dialog_reset_password_email_sent_message", {
          0: email,
        }),
      });
    } catch (error) {
      await alert.show({
        title: t("general_word_error"),
        description: error.message,
      });
    }
  };

  // if state isn't loaded properly yet, don't show broken stuff
  if (!email) return null;

  return (
    <AuthForm<PasswordAuthFlowForm>
      form={form}
      onSubmit={onSubmit}
      className={className}
      footer={(_, disabled) => (
        <div key={"buttons"} css={[buttonsStyle, !exists && justOneStyle]}>
          {exists && (
            <Button
              isFullWidth={!isDesktop}
              size="m"
              variant="transparentPrimary"
              type="button"
              onClick={() => handlePasswordReset()}
            >
              {t("password_input_help_option1")}
            </Button>
          )}
          <AuthSubmitButton disabled={disabled || formDisabled}>
            {t("login_title")}
          </AuthSubmitButton>
        </div>
      )}
    >
      {(form) => (
        <>
          {!hideHeader && (
            <EmailHeader
              title={t("login_title")}
              onBack={() => setFlow("email")}
            />
          )}
          <Text type="headline" size="m" as="h2">
            {t("password_input_headline_existing")}
          </Text>
          <input type="hidden" name="email" value={email} />
          <FormField
            control={form.control}
            name="password"
            showErrorMessage={true}
            defaultValue={""}
            render={({ field }) => (
              <div>
                <AuthTextInput
                  type={visible ? "text" : "password"}
                  required
                  autoFocus
                  autoCorrect="off"
                  autoComplete={exists ? "current-password" : "new-password"}
                  placeholder={t(
                    exists
                      ? "auth_enter_login_password"
                      : "auth_create_your_password",
                  )}
                  button={
                    <PasswordVisibilityButton
                      visible={visible}
                      onClick={() => setVisible(!visible)}
                    />
                  }
                  disabled={formDisabled}
                  {...inputProps}
                  css={fieldsCSS.password}
                  {...field}
                />
                <AuthError css={errorsCSS.password}>
                  {errors?.password?.message ? t(errors.password.message) : ""}
                </AuthError>

                <HCaptcha
                  onError={onCaptchaError}
                  onVerify={onCaptchaVerify}
                  ref={captchaRef}
                  sitekey={process.env.NEXT_PUBLIC_HCAPTCHA_KEY}
                  size="invisible"
                />
              </div>
            )}
            css={inputFormFieldStyle}
          />
        </>
      )}
    </AuthForm>
  );
};

PasswordAuthFlowWithSignup.displayName = "PasswordAuthFlowWithSignup";
